import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Eye,
  EyeOff,
  Save,
  X,
  Upload,
  Loader2,
  Package,
  FolderPlus,
  AlertTriangle,
  Check,
  Image as ImageIcon,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleCategoryActive,
  reorderCategories,
  seedDefaultCategories,
  checkAdmin,
} from '../api/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// ============== SORTABLE CATEGORY ROW ==============
const SortableCategoryRow = ({ category, language, onEdit, onDelete, onToggleActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const trans = category.translations?.[language] || category.translations?.fr || {};

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 ${isDragging ? 'bg-amber-50 opacity-75 shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      <td className="px-4 py-3">
        <button
          className="cursor-grab active:cursor-grabbing p-2 text-gray-400 hover:text-gray-600 rounded"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5" />
        </button>
      </td>
      
      {/* Image */}
      <td className="px-4 py-3">
        {category.image ? (
          <img
            src={category.image}
            alt={trans.name || category.slug}
            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/65/No-Image-Placeholder.svg/1280px-No-Image-Placeholder.svg.png';
            }}
          />
        ) : (
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </td>
      
      {/* Name & Slug */}
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900">{trans.name || category.slug}</p>
        <p className="text-sm text-gray-500">Slug: {category.slug}</p>
      </td>
      
      {/* Description */}
      <td className="px-4 py-3">
        <p className="text-sm text-gray-600 line-clamp-2">
          {trans.description || '-'}
        </p>
      </td>
      
      {/* Product Count */}
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
          <Package className="w-3 h-3" />
          {category.product_count || 0}
        </span>
      </td>
      
      {/* Status */}
      <td className="px-4 py-3 text-center">
        <button
          onClick={() => onToggleActive(category.id)}
          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
            category.active
              ? 'bg-green-100 text-green-700 hover:bg-green-200'
              : 'bg-red-100 text-red-700 hover:bg-red-200'
          }`}
        >
          {category.active ? (
            <>
              <Eye className="w-3 h-3" />
              {language === 'fr' ? 'Actif' : 'Actief'}
            </>
          ) : (
            <>
              <EyeOff className="w-3 h-3" />
              {language === 'fr' ? 'Inactif' : 'Inactief'}
            </>
          )}
        </button>
      </td>
      
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => onEdit(category)}
            className="p-2 text-gray-500 hover:text-amber-700 rounded-lg hover:bg-gray-100"
            title={language === 'fr' ? 'Modifier' : 'Bewerken'}
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(category)}
            className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100"
            title={language === 'fr' ? 'Supprimer' : 'Verwijderen'}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// ============== CATEGORY FORM MODAL ==============
const CategoryFormModal = ({ isOpen, onClose, category, onSave, language }) => {
  const [formData, setFormData] = useState({
    slug: '',
    translations: {
      fr: { name: '', description: '' },
      nl: { name: '', description: '' },
    },
    image: '',
    active: true,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  const fileInputRef = useRef(null);

  const isEdit = !!category;

  // Check Cloudinary status
  useEffect(() => {
    const checkCloudinary = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cloudinary/status`);
        const data = await response.json();
        setCloudinaryEnabled(data.configured);
      } catch (e) {
        setCloudinaryEnabled(false);
      }
    };
    checkCloudinary();
  }, []);

  // Load category data if editing
  useEffect(() => {
    if (category) {
      setFormData({
        slug: category.slug || '',
        translations: category.translations || {
          fr: { name: '', description: '' },
          nl: { name: '', description: '' },
        },
        image: category.image || '',
        active: category.active !== false,
      });
    } else {
      setFormData({
        slug: '',
        translations: {
          fr: { name: '', description: '' },
          nl: { name: '', description: '' },
        },
        image: '',
        active: true,
      });
    }
    setErrors({});
  }, [category, isOpen]);

  const updateTranslation = (lang, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      let imageUrl;
      const token = localStorage.getItem('ebb_token');

      if (cloudinaryEnabled) {
        // Upload to Cloudinary
        const sigResponse = await fetch(`${BACKEND_URL}/api/cloudinary/signature?folder=uploads/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const sigData = await sigResponse.json();

        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('api_key', sigData.api_key);
        formDataUpload.append('timestamp', sigData.timestamp);
        formDataUpload.append('signature', sigData.signature);
        formDataUpload.append('folder', sigData.folder);

        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
          { method: 'POST', body: formDataUpload }
        );

        const result = await uploadResponse.json();
        imageUrl = result.secure_url;
      } else {
        // Fallback to local upload
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload,
        });

        if (!response.ok) throw new Error('Upload failed');
        const data = await response.json();
        imageUrl = `${BACKEND_URL}${data.url}`;
      }

      setFormData((prev) => ({ ...prev, image: imageUrl }));
    } catch (error) {
      console.error('Upload error:', error);
      alert(language === 'fr' ? 'Erreur lors de l\'upload' : 'Upload mislukt');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: '' }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.slug.trim()) {
      newErrors.slug = language === 'fr' ? 'Le slug est requis' : 'Slug is verplicht';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = language === 'fr' 
        ? 'Le slug doit contenir uniquement des lettres minuscules, chiffres et tirets' 
        : 'Slug mag alleen kleine letters, cijfers en streepjes bevatten';
    }

    if (!formData.translations.fr.name.trim()) {
      newErrors.nameFr = language === 'fr' ? 'Le nom français est requis' : 'Franse naam is verplicht';
    }

    if (!formData.translations.nl.name.trim()) {
      newErrors.nameNl = language === 'fr' ? 'Le nom néerlandais est requis' : 'Nederlandse naam is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Save error:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Fout bij opslaan');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit
              ? (language === 'fr' ? 'Modifier la catégorie' : 'Categorie bewerken')
              : (language === 'fr' ? 'Nouvelle catégorie' : 'Nieuwe categorie')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug (identifiant URL) *
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 ${
                errors.slug ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="ex: sweaters, hoodies, jackets"
              disabled={isEdit} // Cannot change slug after creation
            />
            {errors.slug && <p className="mt-1 text-sm text-red-500">{errors.slug}</p>}
            {isEdit && (
              <p className="mt-1 text-xs text-gray-500">
                {language === 'fr' 
                  ? 'Le slug ne peut pas être modifié après création'
                  : 'Slug kan niet worden gewijzigd na aanmaak'}
              </p>
            )}
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'fr' ? 'Image de la catégorie' : 'Categorie afbeelding'}
            </label>
            
            {formData.image ? (
              <div className="relative group w-full h-40">
                <img
                  src={formData.image}
                  alt="Category"
                  className="w-full h-full object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.target.src = 'https://media2.dev.to/dynamic/image/width=1280,height=720,fit=cover,gravity=auto,format=auto/https%3A%2F%2Fdev-to-uploads.s3.amazonaws.com%2Fuploads%2Farticles%2F2es0lwwpd1unf7jjd7p9.png';
                  }}
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                  >
                    <Upload className="w-5 h-5 text-gray-700" />
                  </button>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className={`w-full h-40 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                  uploading ? 'bg-gray-50 pointer-events-none' : 'hover:border-amber-400 hover:bg-amber-50/50'
                } border-gray-300`}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
                    <span className="mt-2 text-sm text-gray-500">
                      {language === 'fr' ? 'Téléchargement...' : 'Uploaden...'}
                    </span>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                    <span className="mt-2 text-sm font-medium text-gray-700">
                      {language === 'fr' ? 'Cliquez pour ajouter une image' : 'Klik om een afbeelding toe te voegen'}
                    </span>
                    <span className="mt-1 text-xs text-gray-500">
                      JPG, PNG, WebP • Max 5MB
                    </span>
                  </>
                )}
              </div>
            )}
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>

          {/* French Name & Description */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              🇫🇷 {language === 'fr' ? 'Textes français' : 'Franse teksten'}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Nom' : 'Naam'} *
              </label>
              <input
                type="text"
                value={formData.translations.fr.name}
                onChange={(e) => updateTranslation('fr', 'name', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 ${
                  errors.nameFr ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ex: Pulls"
              />
              {errors.nameFr && <p className="mt-1 text-sm text-red-500">{errors.nameFr}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                rows={2}
                value={formData.translations.fr.description}
                onChange={(e) => updateTranslation('fr', 'description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="Description de la catégorie..."
              />
            </div>
          </div>

          {/* Dutch Name & Description */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              🇳🇱 {language === 'fr' ? 'Textes néerlandais' : 'Nederlandse teksten'}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Nom' : 'Naam'} *
              </label>
              <input
                type="text"
                value={formData.translations.nl.name}
                onChange={(e) => updateTranslation('nl', 'name', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 ${
                  errors.nameNl ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ex: Truien"
              />
              {errors.nameNl && <p className="mt-1 text-sm text-red-500">{errors.nameNl}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                rows={2}
                value={formData.translations.nl.description}
                onChange={(e) => updateTranslation('nl', 'description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="Categorie beschrijving..."
              />
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-amber-600 focus:ring-amber-500 rounded"
              />
              <span className="text-sm text-gray-700">
                {language === 'fr' ? 'Catégorie active (visible sur le site)' : 'Categorie actief (zichtbaar op de site)'}
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {language === 'fr' ? 'Annuler' : 'Annuleren'}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'fr' ? 'Sauvegarde...' : 'Opslaan...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {language === 'fr' ? 'Sauvegarder' : 'Opslaan'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============== DELETE CONFIRMATION MODAL ==============
const DeleteConfirmModal = ({ isOpen, onClose, category, onConfirm, language }) => {
  const [deleting, setDeleting] = useState(false);

  if (!isOpen || !category) return null;

  const trans = category.translations?.[language] || category.translations?.fr || {};
  const productCount = category.product_count || 0;

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      await onConfirm(category.id);
      onClose();
    } catch (error) {
      console.error('Delete error:', error);
      alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Fout bij verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {language === 'fr' ? 'Confirmer la suppression' : 'Bevestig verwijdering'}
            </h2>
          </div>

          <p className="text-gray-600 mb-4">
            {language === 'fr'
              ? `Êtes-vous sûr de vouloir supprimer la catégorie "${trans.name || category.slug}" ?`
              : `Weet u zeker dat u de categorie "${trans.name || category.slug}" wilt verwijderen?`}
          </p>

          {productCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>{language === 'fr' ? 'Attention :' : 'Let op:'}</strong>{' '}
                {language === 'fr'
                  ? `${productCount} produit(s) utilisent cette catégorie. Ils conserveront le slug "${category.slug}" mais la catégorie ne sera plus visible.`
                  : `${productCount} product(en) gebruiken deze categorie. Ze behouden de slug "${category.slug}" maar de categorie wordt niet meer zichtbaar.`}
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={deleting}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {language === 'fr' ? 'Annuler' : 'Annuleren'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={deleting}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'fr' ? 'Suppression...' : 'Verwijderen...'}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  {language === 'fr' ? 'Supprimer' : 'Verwijderen'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== TOAST NOTIFICATION ==============
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-bottom ${
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      {type === 'success' ? (
        <Check className="w-5 h-5" />
      ) : (
        <AlertTriangle className="w-5 h-5" />
      )}
      {message}
    </div>
  );
};

// ============== MAIN PAGE ==============
const CategoriesPage = () => {
  const navigate = useNavigate();
  const { language } = useStore();
  const t = translations[language];

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check admin auth
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('ebb_token');
      const isAdmin = localStorage.getItem('ebb_admin');

      if (token && isAdmin) {
        try {
          const result = await checkAdmin();
          if (result.is_admin) {
            setIsAuthenticated(true);
          } else {
            navigate('/admin');
          }
        } catch (err) {
          navigate('/admin');
        }
      } else {
        navigate('/admin');
      }
      setCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  // Load categories
  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
      showToast(language === 'fr' ? 'Erreur lors du chargement' : 'Fout bij laden', 'error');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCategories();
    }
  }, [isAuthenticated, loadCategories]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = categories.findIndex((c) => c.id === active.id);
      const newIndex = categories.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(categories, oldIndex, newIndex);
      setCategories(newOrder);

      // Save new order to backend
      try {
        await reorderCategories(newOrder.map((c) => c.id));
        showToast(
          language === 'fr' ? 'Ordre mis à jour' : 'Volgorde bijgewerkt',
          'success'
        );
      } catch (error) {
        console.error('Error reordering:', error);
        // Revert on error
        loadCategories();
        showToast(
          language === 'fr' ? 'Erreur lors de la réorganisation' : 'Fout bij herschikken',
          'error'
        );
      }
    }
  };

  // Open create modal
  const handleCreate = () => {
    setEditingCategory(null);
    setIsFormModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (category) => {
    setEditingCategory(category);
    setIsFormModalOpen(true);
  };

  // Save category (create or update)
  const handleSave = async (formData) => {
    console.log('[DEBUG] handleSave called with formData:', formData);
    
    if (editingCategory) {
      // Update
      console.log('[DEBUG] Updating category:', editingCategory.id);
      const result = await updateCategory(editingCategory.id, formData);
      console.log('[DEBUG] Update result:', result);
      setCategories((prev) =>
        prev.map((c) => (c.id === editingCategory.id ? result.category : c))
      );
      showToast(
        language === 'fr' ? 'Catégorie mise à jour' : 'Categorie bijgewerkt',
        'success'
      );
    } else {
      // Create
      console.log('[DEBUG] Creating new category');
      const result = await createCategory(formData);
      console.log('[DEBUG] Create result:', result);
      if (result && result.category) {
        setCategories((prev) => [...prev, result.category]);
        showToast(
          language === 'fr' ? 'Catégorie créée' : 'Categorie aangemaakt',
          'success'
        );
      } else {
        console.error('[DEBUG] Unexpected result format:', result);
        throw new Error('Unexpected response format');
      }
    }
  };

  // Open delete confirmation
  const handleDeleteClick = (category) => {
    setDeletingCategory(category);
    setIsDeleteModalOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async (categoryId) => {
    await deleteCategory(categoryId);
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));
    showToast(
      language === 'fr' ? 'Catégorie supprimée' : 'Categorie verwijderd',
      'success'
    );
  };

  // Toggle active status
  const handleToggleActive = async (categoryId) => {
    try {
      const result = await toggleCategoryActive(categoryId);
      setCategories((prev) =>
        prev.map((c) => (c.id === categoryId ? result.category : c))
      );
      showToast(
        result.category.active
          ? (language === 'fr' ? 'Catégorie activée' : 'Categorie geactiveerd')
          : (language === 'fr' ? 'Catégorie désactivée' : 'Categorie gedeactiveerd'),
        'success'
      );
    } catch (error) {
      console.error('Error toggling active:', error);
      showToast(
        language === 'fr' ? 'Erreur lors du changement de statut' : 'Fout bij statuswijziging',
        'error'
      );
    }
  };

  // Seed default categories
  const handleSeedCategories = async () => {
    if (!window.confirm(
      language === 'fr'
        ? 'Créer les catégories par défaut basées sur les produits existants ?'
        : 'Standaardcategorieën aanmaken op basis van bestaande producten?'
    )) return;

    try {
      const result = await seedDefaultCategories();
      setCategories(result.categories);
      showToast(
        language === 'fr'
          ? `${result.categories.length} catégories créées`
          : `${result.categories.length} categorieën aangemaakt`,
        'success'
      );
    } catch (error) {
      console.error('Error seeding:', error);
      showToast(
        error.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Fout'),
        'error'
      );
    }
  };

  if (checkingAuth || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent"></div>
      </div>
    );
  }

  const hasLegacyCategories = categories.some((c) => c.legacy);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-amber-900 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-amber-200 hover:text-white mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t.common.back}
          </button>
          <h1 className="text-3xl font-bold text-white">
            {language === 'fr' ? 'Gestion des catégories' : 'Categoriebeheer'}
          </h1>
          <p className="text-amber-200 mt-1">
            {language === 'fr'
              ? 'Créez, modifiez et réorganisez vos catégories de produits'
              : 'Maak, bewerk en herschik uw productcategorieën'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legacy categories notice */}
        {hasLegacyCategories && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-yellow-800 font-medium">
                  {language === 'fr'
                    ? 'Catégories legacy détectées'
                    : 'Legacy categorieën gedetecteerd'}
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  {language === 'fr'
                    ? 'Les catégories actuelles sont basées sur les produits. Cliquez ci-dessous pour créer des catégories gérables.'
                    : 'Huidige categorieën zijn gebaseerd op producten. Klik hieronder om beheerbare categorieën aan te maken.'}
                </p>
                <button
                  onClick={handleSeedCategories}
                  className="mt-2 inline-flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  <FolderPlus className="w-4 h-4" />
                  {language === 'fr' ? 'Créer les catégories' : 'Categorieën aanmaken'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white rounded-lg shadow">
          {/* Toolbar */}
          <div className="p-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {language === 'fr' ? 'Catégories' : 'Categorieën'} ({categories.filter(c => !c.legacy).length})
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
              {language === 'fr' ? 'Nouvelle catégorie' : 'Nieuwe categorie'}
            </button>
          </div>

          {/* Categories table */}
          {categories.filter(c => !c.legacy).length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {language === 'fr'
                  ? 'Aucune catégorie créée'
                  : 'Geen categorieën aangemaakt'}
              </p>
              {!hasLegacyCategories && (
                <button
                  onClick={handleSeedCategories}
                  className="mt-4 inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium"
                >
                  <FolderPlus className="w-4 h-4" />
                  {language === 'fr'
                    ? 'Créer des catégories par défaut'
                    : 'Standaardcategorieën aanmaken'}
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                        {/* Drag handle */}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Image
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {language === 'fr' ? 'Nom' : 'Naam'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {language === 'fr' ? 'Produits' : 'Producten'}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {language === 'fr' ? 'Statut' : 'Status'}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <SortableContext
                      items={categories.filter(c => !c.legacy).map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {categories.filter(c => !c.legacy).map((category) => (
                        <SortableCategoryRow
                          key={category.id}
                          category={category}
                          language={language}
                          onEdit={handleEdit}
                          onDelete={handleDeleteClick}
                          onToggleActive={handleToggleActive}
                        />
                      ))}
                    </SortableContext>
                  </tbody>
                </table>
              </DndContext>
            </div>
          )}

          {/* Drag hint */}
          {categories.filter(c => !c.legacy).length > 1 && (
            <div className="px-4 py-3 bg-gray-50 border-t text-center">
              <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                <GripVertical className="w-4 h-4" />
                {language === 'fr'
                  ? 'Glissez-déposez les lignes pour réorganiser les catégories'
                  : 'Sleep en plaats rijen om categorieën te herschikken'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      <CategoryFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSave}
        language={language}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingCategory(null);
        }}
        category={deletingCategory}
        onConfirm={handleDeleteConfirm}
        language={language}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CategoriesPage;
